import { useEffect, useRef, useState } from "react";
import { Check, ChevronsUpDown, SearchX, X } from "lucide-react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../ui/command";


export const SearchableLicensedUserCombobox = ({ users, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const selectedUser = users.find((user) => user.id === value);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative flex min-w-56 flex-1 gap-2" data-testid="licensed-user-combobox">
      <div className="relative min-w-0 flex-1">
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label="Choose licensed user"
          onClick={() => setOpen((current) => !current)}
          className="input-dark flex w-full min-w-0 items-center justify-between gap-3 text-left"
          data-testid="bulk-user-select"
        >
          <span className={selectedUser ? "truncate text-slate-100" : "truncate text-slate-500"} data-testid="selected-licensed-username">
            {selectedUser?.username || "Choose licensed user"}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-slate-500" />
        </button>
        {open && (
          <div className="absolute left-0 top-[calc(100%+4px)] z-50 w-full min-w-64 overflow-hidden rounded-md border border-slate-700 bg-slate-950 shadow-xl" data-testid="licensed-user-search-menu">
          <Command>
            <CommandInput placeholder="Search username..." data-testid="licensed-user-search-input" />
            <CommandList>
              <CommandEmpty data-testid="licensed-user-search-empty">
                <SearchX className="mx-auto mb-2 h-4 w-4 text-slate-500" />
                No licensed username found.
              </CommandEmpty>
              <CommandGroup heading={`${users.length} licensed users`}>
                {users.map((user) => (
                  <CommandItem
                    key={user.id}
                    value={user.username}
                    onSelect={() => {
                      onChange(user.id);
                      setOpen(false);
                    }}
                    className="text-slate-200"
                    data-testid={`licensed-user-option-${user.id}`}
                  >
                    <Check className={`h-4 w-4 ${value === user.id ? "opacity-100 text-cyan-400" : "opacity-0"}`} />
                    <span className="truncate">{user.username}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
          </div>
        )}
      </div>
      {selectedUser && (
        <button
          type="button"
          onClick={() => onChange("")}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-slate-700 text-slate-400 transition-colors hover:border-rose-500/40 hover:text-rose-300"
          aria-label="Clear selected licensed user"
          data-testid="clear-licensed-user-button"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};